import React, { useState, useEffect } from "react";
import {
  Users,
  FileText,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Activity,
  UserCheck,
  UserX,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Pill,
  Stethoscope,
  CreditCard,
  BarChart3,
  IndianRupee,
  RefreshCw,
  Eye,
  Target,
  Award,
} from "lucide-react";
// Note: You'll need to import your API service
import API from "../../../services/interceptor";
// import user from "../../../assets/image/user.jpg";
// import logo from "../../../assets/image/kkgt-header-image.jpg";

// Mock API for demonstration - replace with your actual API
// const API = {
//   get: (url) => {
//     return new Promise((resolve) => {
//       setTimeout(() => {
//         // Mock data that simulates your backend response structure
//         const mockData = {
//           "/api/admin/dashboard-stats": {
//             data: {
//               data: {
//                 patients: { total: 1250, today: 15, active: 1100, thisMonth: 85, inactive: 150 },
//                 opds: { total: 2850, today: 32, thisMonth: 465, thisYear: 2850 },
//                 appointments: { total: 3200, today: 18, byStatus: { pending: 45, completed: 2800, cancelled: 355 } },
//                 revenue: { total: { received: 2850000, pending: 185000 } },
//                 billing: { today: { totalReceived: 45500 }, thisMonth: { totalReceived: 875000 } }
//               }
//             }
//           },
//           "/api/admin/patient-stats": {
//             data: {
//               data: {
//                 demographics: {
//                   gender: [{ _id: "Male", count: 650 }, { _id: "Female", count: 580 }, { _id: "Other", count: 20 }],
//                   ageGroups: [{ _id: "0-18", count: 180 }, { _id: "19-35", count: 420 }, { _id: "36-50", count: 380 }, { _id: "51+", count: 270 }]
//                 }
//               }
//             }
//           },
//           "/api/admin/financial-report": {
//             data: {
//               data: {
//                 topTreatments: [
//                   { _id: "Root Canal", totalRevenue: 450000 },
//                   { _id: "Dental Implants", totalRevenue: 380000 },
//                   { _id: "Orthodontics", totalRevenue: 320000 },
//                   { _id: "Teeth Cleaning", totalRevenue: 180000 },
//                   { _id: "Tooth Extraction", totalRevenue: 120000 }
//                 ],
//                 outstandingPayments: [
//                   { patientName: "John Doe", opdNumber: "OPD001", pendingAmount: 15000 },
//                   { patientName: "Jane Smith", opdNumber: "OPD002", pendingAmount: 25000 },
//                   { patientName: "Mike Johnson", opdNumber: "OPD003", pendingAmount: 8500 }
//                 ],
//                 summary: { totalOutstanding: 185000, totalPatients: 45 }
//               }
//             }
//           },
//           "/api/admin/treatment-analysis": {
//             data: {
//               data: {
//                 commonDiagnoses: [
//                   { _id: "Dental Caries", count: 320 },
//                   { _id: "Gingivitis", count: 280 },
//                   { _id: "Periodontitis", count: 150 },
//                   { _id: "Tooth Sensitivity", count: 120 },
//                   { _id: "Oral Ulcers", count: 85 }
//                 ],
//                 commonMedications: [
//                   { _id: "Amoxicillin", count: 180 },
//                   { _id: "Ibuprofen", count: 165 },
//                   { _id: "Paracetamol", count: 140 },
//                   { _id: "Metronidazole", count: 95 },
//                   { _id: "Chlorhexidine", count: 78 }
//                 ],
//                 treatmentCompletion: { completionRate: 87, total: 2800, completed: 2436 }
//               }
//             }
//           }
//         };
//         resolve(mockData[url]);
//       }, 1000);
//     });
//   }
// };

const AdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [patientStats, setPatientStats] = useState(null);
  const [financialReport, setFinancialReport] = useState(null);
  const [treatmentAnalysis, setTreatmentAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [error, setError] = useState(null);
  const [dataRefreshTime, setDataRefreshTime] = useState(new Date());

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [dashboard, patients, financial, treatment] = await Promise.all([
        API.get("/api/admin/dashboard-stats"),
        API.get("/api/admin/patient-stats"),
        API.get("/api/admin/financial-report"),
        API.get("/api/admin/treatment-analysis"),
      ]);

      setDashboardData(dashboard.data.data);
      setPatientStats(patients.data.data);
      setFinancialReport(financial.data.data);
      setTreatmentAnalysis(treatment.data.data);
      setDataRefreshTime(new Date());
    } catch (err) {
      setError(err.message);
      console.error("Error fetching dashboard data:", err);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({
    title,
    value,
    subtitle,
    icon: Icon,
    trend,
    color = "blue",
    isHighlighted = false,
  }) => {
    const colorClasses = {
      blue: isHighlighted
        ? "bg-blue-600 text-white shadow-lg"
        : "bg-blue-50 border-blue-200 text-blue-600",
      green: isHighlighted
        ? "bg-green-600 text-white shadow-lg"
        : "bg-green-50 border-green-200 text-green-600",
      yellow: isHighlighted
        ? "bg-yellow-600 text-white shadow-lg"
        : "bg-yellow-50 border-yellow-200 text-yellow-600",
      red: isHighlighted
        ? "bg-red-600 text-white shadow-lg"
        : "bg-red-50 border-red-200 text-red-600",
      purple: isHighlighted
        ? "bg-purple-600 text-white shadow-lg"
        : "bg-purple-50 border-purple-200 text-purple-600",
      indigo: isHighlighted
        ? "bg-indigo-600 text-white shadow-lg"
        : "bg-indigo-50 border-indigo-200 text-indigo-600",
    };

    return (
      <div
        className={`${
          isHighlighted
            ? "bg-gradient-to-br from-blue-600 to-blue-700 text-white border-0"
            : "bg-white border border-gray-200"
        } rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-200 transform hover:scale-105`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p
              className={`text-sm font-medium ${
                isHighlighted ? "text-blue-100" : "text-gray-600"
              } mb-1`}
            >
              {title}
            </p>
            <p
              className={`text-3xl font-bold ${
                isHighlighted ? "text-white" : "text-gray-900"
              } mb-1`}
            >
              {value}
            </p>
            {subtitle && (
              <p
                className={`text-sm ${
                  isHighlighted ? "text-blue-100" : "text-gray-500"
                }`}
              >
                {subtitle}
              </p>
            )}
          </div>
          <div
            className={`p-4 rounded-full ${
              isHighlighted ? "bg-white bg-opacity-20" : colorClasses[color]
            } ml-4`}
          >
            <Icon size={28} className={isHighlighted ? "text-white" : ""} />
          </div>
        </div>
        {trend && (
          <div className="mt-4 flex items-center">
            {trend > 0 ? (
              <TrendingUp
                className={isHighlighted ? "text-green-200" : "text-green-500"}
                size={18}
              />
            ) : (
              <TrendingDown
                className={isHighlighted ? "text-red-200" : "text-red-500"}
                size={18}
              />
            )}
            <span
              className={`text-sm ml-2 font-medium ${
                isHighlighted
                  ? trend > 0
                    ? "text-green-200"
                    : "text-red-200"
                  : trend > 0
                  ? "text-green-600"
                  : "text-red-600"
              }`}
            >
              {Math.abs(trend)}% from last month
            </span>
          </div>
        )}
      </div>
    );
  };

  // Today's Overview Section
  const TodayOverview = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Today's Overview</h2>
          <p className="text-gray-600 mt-1">
            Real-time statistics for {new Date().toLocaleDateString('en-GB')}
          </p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <Clock size={16} />
          <span>Last updated: {dataRefreshTime.toLocaleTimeString()}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Today's Patients"
          value={dashboardData?.patients?.today || 0}
          subtitle="New registrations"
          icon={Users}
          color="blue"
          isHighlighted={true}
        />
        <StatCard
          title="Today's OPDs"
          value={dashboardData?.opds?.today || 0}
          subtitle="Total consultations"
          icon={FileText}
          color="green"
          isHighlighted={true}
        />
        <StatCard
          title="Today's Appointments"
          value={dashboardData?.appointments?.today || 0}
          subtitle="Scheduled visits"
          icon={Calendar}
          color="purple"
          isHighlighted={true}
        />
        <StatCard
          title="Today's Revenue"
          value={`₹${(
            dashboardData?.billing?.today?.totalReceived || 0
          ).toLocaleString()}`}
          subtitle="Cash collected"
          icon={IndianRupee}
          color="indigo"
          isHighlighted={true}
        />
      </div>
    </div>
  );

  const OverviewTab = () => (
    <div className="space-y-8">
      <TodayOverview />

      {/* Patient Statistics */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <Users className="mr-3" size={24} />
          Patient Statistics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Patients"
            value={dashboardData?.patients?.total || 0}
            subtitle="Lifetime registrations"
            icon={Users}
            color="blue"
            trend={8.5}
          />
          <StatCard
            title="Active Patients"
            value={dashboardData?.patients?.active || 0}
            subtitle="Verified accounts"
            icon={UserCheck}
            color="green"
            trend={12.3}
          />
          <StatCard
            title="This Month"
            value={dashboardData?.patients?.thisMonth || 0}
            subtitle="New registrations"
            icon={TrendingUp}
            color="purple"
            trend={-2.1}
          />
          <StatCard
            title="Inactive Patients"
            value={dashboardData?.patients?.inactive || 0}
            subtitle="Pending verification"
            icon={UserX}
            color="yellow"
          />
        </div>
      </div>

      {/* OPD Statistics */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <FileText className="mr-3" size={24} />
          OPD Performance
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total OPDs"
            value={dashboardData?.opds?.total || 0}
            subtitle="All time consultations"
            icon={FileText}
            color="indigo"
            trend={15.2}
          />
          <StatCard
            title="This Month"
            value={dashboardData?.opds?.thisMonth || 0}
            subtitle="Monthly consultations"
            icon={Calendar}
            color="blue"
            trend={7.8}
          />
          <StatCard
            title="This Year"
            value={dashboardData?.opds?.thisYear || 0}
            subtitle="Yearly consultations"
            icon={BarChart3}
            color="green"
            trend={22.5}
          />
          <StatCard
            title="Daily Average"
            value={Math.round((dashboardData?.opds?.thisMonth || 0) / 30)}
            subtitle="Monthly average"
            icon={Activity}
            color="purple"
          />
        </div>
      </div>

      {/* Appointment Management */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <Calendar className="mr-3" size={24} />
          Appointment Management
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Appointments"
            value={dashboardData?.appointments?.total || 0}
            subtitle="All bookings"
            icon={Calendar}
            color="blue"
            trend={5.7}
          />
          <StatCard
            title="Pending"
            value={dashboardData?.appointments?.byStatus?.pending || 0}
            subtitle="Awaiting confirmation"
            icon={Clock}
            color="yellow"
          />
          <StatCard
            title="Completed"
            value={dashboardData?.appointments?.byStatus?.completed || 0}
            subtitle="Successfully finished"
            icon={CheckCircle}
            color="green"
          />
          <StatCard
            title="Cancelled"
            value={dashboardData?.appointments?.byStatus?.cancelled || 0}
            subtitle="Cancelled bookings"
            icon={XCircle}
            color="red"
          />
        </div>
      </div>

      {/* Financial Overview */}
      <div>
        <h3 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
          <DollarSign className="mr-3" size={24} />
          Financial Overview
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Revenue"
            value={`₹${(
              dashboardData?.revenue?.total?.received || 0
            ).toLocaleString()}`}
            subtitle="Lifetime collection"
            icon={Award}
            color="green"
            trend={18.9}
          />
          <StatCard
            title="This Month"
            value={`₹${(
              dashboardData?.billing?.thisMonth?.totalReceived || 0
            ).toLocaleString()}`}
            subtitle="Monthly collection"
            icon={TrendingUp}
            color="purple"
            trend={11.2}
          />
          <StatCard
            title="Outstanding"
            value={`₹${(
              dashboardData?.revenue?.total?.pending || 0
            ).toLocaleString()}`}
            subtitle="Pending payments"
            icon={AlertCircle}
            color="red"
          />
          <StatCard
            title="Collection Rate"
            value={`${(
              ((dashboardData?.revenue?.total?.received || 0) /
                ((dashboardData?.revenue?.total?.received || 0) +
                  (dashboardData?.revenue?.total?.pending || 0))) *
              100
            ).toFixed(1)}%`}
            subtitle="Payment efficiency"
            icon={Target}
            color="indigo"
            trend={3.2}
          />
        </div>
      </div>
    </div>
  );

  const PatientAnalyticsTab = () => (
    <div className="space-y-8">
      <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
        <Users className="mr-3" size={28} />
        Patient Analytics
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Gender Distribution */}
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow">
          <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Eye className="mr-2" size={20} />
            Gender Distribution
          </h4>
          <div className="space-y-4">
            {patientStats?.demographics?.gender?.map((item, index) => {
              const total =
                patientStats?.demographics?.gender?.reduce(
                  (sum, g) => sum + g.count,
                  0
                ) || 1;
              const percentage = ((item.count / total) * 100).toFixed(1);
              return (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <span className="text-sm font-medium text-gray-700">
                    {item._id || "Not Specified"}
                  </span>
                  <div className="text-right">
                    <span className="text-lg font-bold text-gray-900 block">
                      {item.count}
                    </span>
                    <span className="text-xs text-gray-500">{percentage}%</span>
                  </div>
                </div>
              );
            }) || (
              <div className="text-gray-500 text-center py-4">
                No data available
              </div>
            )}
          </div>
        </div>

        {/* Age Groups */}
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow">
          <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <BarChart3 className="mr-2" size={20} />
            Age Distribution
          </h4>
          <div className="space-y-4">
            {patientStats?.demographics?.ageGroups?.map((item, index) => {
              const total =
                patientStats?.demographics?.ageGroups?.reduce(
                  (sum, g) => sum + g.count,
                  0
                ) || 1;
              const percentage = ((item.count / total) * 100).toFixed(1);
              return (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <span className="text-sm font-medium text-gray-700">
                    {item._id} years
                  </span>
                  <div className="text-right">
                    <span className="text-lg font-bold text-gray-900 block">
                      {item.count}
                    </span>
                    <span className="text-xs text-gray-500">{percentage}%</span>
                  </div>
                </div>
              );
            }) || (
              <div className="text-gray-500 text-center py-4">
                No data available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const FinancialTab = () => (
    <div className="space-y-8">
      <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
        <IndianRupee className="mr-3" size={28} />
        Financial Analysis
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Top Treatments */}
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow">
          <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Award className="mr-2" size={20} />
            Top Revenue Generators
          </h4>
          <div className="space-y-4">
            {financialReport?.topTreatments?.slice(0, 5).map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 border-l-4 border-green-500 bg-green-50 rounded-r-lg"
              >
                <div>
                  <span className="text-sm font-semibold text-gray-800">
                    {item._id}
                  </span>
                  <p className="text-xs text-gray-600 mt-1">
                    Treatment #{index + 1}
                  </p>
                </div>
                <span className="text-lg font-bold text-green-700">
                  ₹{item.totalRevenue.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Outstanding Payments */}
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow">
          <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <AlertCircle className="mr-2" size={20} />
            Priority Collections
          </h4>
          <div className="space-y-4 max-h-80 overflow-y-auto">
            {financialReport?.outstandingPayments
              ?.slice(0, 5)
              .map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-4 border border-red-200 bg-red-50 rounded-lg"
                >
                  <div>
                    <span className="text-sm font-semibold text-gray-800">
                      {item.patientName}
                    </span>
                    <p className="text-xs text-gray-600 mt-1">
                      {item.opdNumber}
                    </p>
                  </div>
                  <span className="text-lg font-bold text-red-600">
                    ₹{item.pendingAmount.toLocaleString()}
                  </span>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 text-white shadow-lg">
        <h4 className="text-xl font-semibold mb-6">
          Outstanding Payments Summary
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="text-center p-6 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm">
            <p className="text-blue-100 mb-2">Total Outstanding</p>
            <p className="text-3xl font-bold">
              ₹
              {(
                financialReport?.summary?.totalOutstanding || 0
              ).toLocaleString()}
            </p>
          </div>
          <div className="text-center p-6 bg-white bg-opacity-20 rounded-lg backdrop-blur-sm">
            <p className="text-blue-100 mb-2">Affected Patients</p>
            <p className="text-3xl font-bold">
              {financialReport?.summary?.totalPatients || 0}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const TreatmentTab = () => (
    <div className="space-y-8">
      <h3 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
        <Stethoscope className="mr-3" size={28} />
        Treatment Analysis
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Common Diagnoses */}
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow">
          <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Stethoscope className="mr-2" size={20} />
            Common Diagnoses
          </h4>
          <div className="space-y-3">
            {treatmentAnalysis?.commonDiagnoses
              ?.slice(0, 5)
              .map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <span className="text-sm font-medium text-gray-700">
                    {item._id}
                  </span>
                  <span className="text-lg font-bold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                    {item.count}
                  </span>
                </div>
              ))}
          </div>
        </div>

        {/* Common Medications */}
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm hover:shadow-md transition-shadow">
          <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
            <Pill className="mr-2" size={20} />
            Common Medications
          </h4>
          <div className="space-y-3">
            {treatmentAnalysis?.commonMedications
              ?.slice(0, 5)
              .map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <span className="text-sm font-medium text-gray-700">
                    {item._id}
                  </span>
                  <span className="text-lg font-bold text-green-600 bg-green-100 px-3 py-1 rounded-full">
                    {item.count}
                  </span>
                </div>
              ))}
          </div>
        </div>

        {/* Treatment Completion */}
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-8 text-white shadow-lg">
          <h4 className="text-lg font-semibold mb-6 flex items-center">
            <CheckCircle className="mr-2" size={20} />
            Treatment Success
          </h4>
          <div className="text-center">
            <div className="text-4xl font-bold mb-3">
              {treatmentAnalysis?.treatmentCompletion?.completionRate || 0}%
            </div>
            <p className="text-green-100 mb-6">Success Rate</p>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-3 bg-white bg-opacity-20 rounded-lg">
                <span>Total Treatments:</span>
                <span className="font-bold">
                  {treatmentAnalysis?.treatmentCompletion?.total || 0}
                </span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white bg-opacity-20 rounded-lg">
                <span>Successful:</span>
                <span className="font-bold">
                  {treatmentAnalysis?.treatmentCompletion?.completed || 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <XCircle className="text-red-500 mx-auto mb-4" size={48} />
          <div className="text-red-600 text-xl mb-4 font-semibold">
            Error loading dashboard
          </div>
          <button
            onClick={fetchDashboardData}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Retry Loading
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Professional Header */}
        <div className="bg-white shadow-lg border-b-4 border-blue-600 rounded-xl mb-8">
          <div className="px-6 sm:px-8 py-6">
            <div className="flex items-center justify-center space-x-4 bg-gradient-to-r from-blue-600 to-indigo-700 px-6 py-4 rounded-xl flex-1">
              <div className="h-14 w-14 bg-white rounded-full flex items-center justify-center shadow-md">
                <Stethoscope className="text-blue-600" size={28} />
              </div>
              <div>
                <h1 className="text-white font-bold text-2xl">
                  Mahalaxmi Dental Clinic
                </h1>
                <p className="text-blue-100 text-sm">
                  Professional Healthcare Dashboard
                </p>
              </div>
            </div>

            <div className="flex justify-end mt-5">
              <button
                onClick={fetchDashboardData}
                disabled={loading}
                className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 font-medium shadow-md"
              >
                <RefreshCw
                  className={loading ? "animate-spin" : ""}
                  size={18}
                />
                <span>{loading ? "Refreshing..." : "Refresh Data"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Enhanced Tab Navigation */}
        <div className="mb-8">
          <nav className="flex flex-wrap bg-white rounded-xl shadow-sm border border-gray-200 p-2">
            {[
              { key: "overview", label: "Overview", icon: BarChart3 },
              { key: "patients", label: "Patient Analytics", icon: Users },
              { key: "financial", label: "Financial", icon: IndianRupee },
              {
                key: "treatment",
                label: "Treatment Analysis",
                icon: Stethoscope,
              },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  activeTab === key
                    ? "bg-blue-600 text-white shadow-lg transform scale-105"
                    : "text-gray-600 hover:text-blue-600 hover:bg-blue-50"
                }`}
              >
                <Icon className="mr-2" size={18} />
                {label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="mb-8">
          {activeTab === "overview" && <OverviewTab />}
          {activeTab === "patients" && <PatientAnalyticsTab />}
          {activeTab === "financial" && <FinancialTab />}
          {activeTab === "treatment" && <TreatmentTab />}
        </div>

        {/* Professional Footer */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 text-center">
          <div className="flex items-center justify-center space-x-4 text-gray-600">
            <Activity size={20} />
            <span className="text-sm">
              Dashboard last updated: {dataRefreshTime.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
